import { useState } from 'react'
import * as React from 'react'
import { router } from '@inertiajs/react'
import { Card, CardContent } from '@/components/ui/card'
import { TagLabel } from '@/components/ui/tag-label'
import { CircleNumber } from '@/components/ui/circle-number'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import WorkflowStats from './WorkflowStats'
import { GripVertical, Edit, Trash2, MoreVertical, Copy } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface WorkflowCardProps {
  workflow: {
    id: number
    name: string
    description: string | null
    order: number
    isActive: boolean
    stopProcessingOnMatch: boolean
    stopProcessingOnFail: boolean
    filtersCount: number
    actionsCount: number
    stats: {
      emailsProcessed: number
      emailsMatched: number
      emailsFailed: number
      matchRate: number
      failRate: number
      lastProcessedAt: string | null
    }
  }
  accountId: number
  onEdit: (id: number) => void
  onDelete?: (id: number) => void
  dragHandleProps?: any
}

export default function WorkflowCard({
  workflow,
  accountId,
  onEdit,
  onDelete,
  dragHandleProps,
}: WorkflowCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const [localIsActive, setLocalIsActive] = useState(workflow.isActive)

  // Sync local state with prop when workflow changes
  React.useEffect(() => {
    setLocalIsActive(workflow.isActive)
  }, [workflow.isActive])

  const handleToggle = (checked: boolean) => {
    // Optimistically update local state immediately
    setLocalIsActive(checked)
    setIsToggling(true)

    router.patch(
      `/accounts/${accountId}/workflows/${workflow.id}`,
      {
        workflow: {
          is_active: checked,
        },
      },
      {
        preserveScroll: true,
        only: ['workflows'],
        onFinish: () => {
          setIsToggling(false)
          // Reset to prop value if update failed
          setLocalIsActive(workflow.isActive)
        },
        onError: () => {
          // Revert on error
          setLocalIsActive(workflow.isActive)
          setIsToggling(false)
        },
      }
    )
  }

  const handleDelete = () => {
    // Optimistically remove from list
    if (onDelete) {
      onDelete(workflow.id)
    }

    router.delete(`/accounts/${accountId}/workflows/${workflow.id}`, {
      preserveScroll: true,
      onError: () => {
        // Reload on error to restore the workflow
        router.reload({ only: ['workflows'] })
      },
    })
  }

  const handleDuplicate = () => {
    router.post(`/accounts/${accountId}/workflows/${workflow.id}/duplicate`, {}, {
      preserveScroll: true,
      onSuccess: () => {
        router.reload({ only: ['workflows'] })
      },
      onError: () => {
        router.reload({ only: ['workflows'] })
      },
    })
  }

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    if (
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('[role="button"]') ||
      (e.target as HTMLElement).closest('[data-drag-handle]')
    ) {
      return
    }
    router.get(`/accounts/${accountId}/workflows/${workflow.id}`)
  }

  return (
    <>
      <Card className={`relative cursor-pointer hover:shadow-md transition-shadow rounded-lg ${!localIsActive ? 'opacity-40' : ''}`} onClick={handleCardClick}>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {/* Drag Handle */}
            <div
              {...dragHandleProps}
              data-drag-handle
              className="cursor-grab active:cursor-grabbing pt-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <GripVertical className="w-5 h-5" />
            </div>

            {/* Order Number */}
            <div className="flex-shrink-0">
              <CircleNumber
                number={workflow.order}
                bgColor="bg-blue-100 dark:bg-blue-900"
                textColor="text-blue-700 dark:text-blue-300"
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 w-full sm:w-auto">
              <div className="mb-2">
                <h3 className="text-lg font-semibold text-blue-950 dark:text-white mb-1">
                  {workflow.name}
                </h3>
                {workflow.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {workflow.description}
                  </p>
                )}
              </div>

              {/* Stats */}
              <WorkflowStats
                emailsProcessed={workflow.stats.emailsProcessed}
                emailsMatched={workflow.stats.emailsMatched}
                emailsFailed={workflow.stats.emailsFailed}
                matchRate={workflow.stats.matchRate}
                failRate={workflow.stats.failRate}
                filtersCount={workflow.filtersCount}
                actionsCount={workflow.actionsCount}
                showFiltersAndActions={true}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-start sm:justify-end" onClick={(e) => e.stopPropagation()}>
              {/* Status Badges */}
              {workflow.stopProcessingOnMatch && (
                <TagLabel textColor="text-orange-600 dark:text-orange-400">
                  Stops on match
                </TagLabel>
              )}
              {workflow.stopProcessingOnFail && (
                <TagLabel textColor="text-purple-600 dark:text-purple-400">
                  Stops on fail
                </TagLabel>
              )}
              <Switch
                checked={localIsActive}
                onCheckedChange={handleToggle}
                disabled={isToggling}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(workflow.id)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDuplicate}>
                    <Copy className="w-4 h-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{workflow.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
